import { useState, useEffect } from 'react'
import { supabase } from '../../services/supabase'
import { useClients } from '../../hooks/useClients'
import { Table } from '../../components/Common/Table'
import { Pagination } from '../../components/Common/Pagination'
import { Button } from '../../components/Common/Button'
import { ClientForm } from './ClientForm'
import { ClientImport } from './ClientImport'
import { ClientCodeMatcher } from './ClientCodeMatcher'
import { IzuluDuplicateModal } from './IzuluDuplicateModal'

// Export headers exactly matching what ClientImport AUTO_MAP expects
const CLIENT_EXPORT_HEADERS = [
  'Customer Code', 'Full Name', 'Surname', 'ID Number', 'Mobile', 'Email',
  'Date of Birth', 'Status', 'Active',
]

export function ClientsList({ sidebarCollapsed, isDarkMode = false }) {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('updated_at:desc')
  const [showForm, setShowForm] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editingClient, setEditingClient] = useState(null)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateCandidates, setDuplicateCandidates] = useState([])
  const [pendingFormData, setPendingFormData] = useState(null)
  const [checkingDuplicates] = useState(false)
  const [showMatcher, setShowMatcher] = useState(false)
  // Advisors for bulk assignment and filtering
  const [advisors, setAdvisors] = useState([])
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [assignAdvisorId, setAssignAdvisorId] = useState('')
  const [assigning, setAssigning] = useState(false)
  const [selectedAdvisorFilter, setSelectedAdvisorFilter] = useState(null)
  const [deletingSelected, setDeletingSelected] = useState(false)
  const [showDuplicateFinder, setShowDuplicateFinder] = useState(false)
  const [duplicateGroups, setDuplicateGroups] = useState([])
  const [duplicatesLoading, setDuplicatesLoading] = useState(false)
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set())
  const [deletingDuplicates, setDeletingDuplicates] = useState(false)

  const [sortBy, sortOrder] = sort.split(':')
  const [relinking, setRelinking] = useState(false)
  const { clients, total, loading, createClient, updateClient, getClientById, toggleActive, importClients, relinkPoliciesByCode, bulkAssignAdvisor, refetch } = useClients(page, 100, search, sortBy, sortOrder, selectedAdvisorFilter?.id || null)

  // Load advisors
  useEffect(() => {
    supabase.from('advisors').select('id, full_name').eq('active', true).order('full_name')
      .then(({ data }) => setAdvisors(data || []))
  }, [])

  const handleRelink = async () => {
    setRelinking(true)
    try {
      const count = await relinkPoliciesByCode()
      await refetch()
      alert(`Done — ${count} policies relinked to clients by Customer Code.`)
    } catch (err) {
      alert(`Relink failed: ${err.message}`)
    } finally {
      setRelinking(false)
    }
  }

  const handleRowClick = async (row) => {
    try {
      const full = await getClientById(row.id)
      setEditingClient(full)
      setShowForm(true)
    } catch (err) {
      console.error('Error loading client:', err)
    }
  }

  const saveAndSync = async (formData) => {
    const { clientData, addressData, maritalData, bankingData, relativesData } = formData
    try {
      await createClient(clientData, addressData, maritalData, bankingData, relativesData)

      setShowForm(false)
      setEditingClient(null)
    } catch (err) {
      console.error('Error saving client:', err)
      alert(`Save failed: ${err.message}`)
    }
  }

  const handleSubmit = async (data) => {
    const { clientData, addressData, maritalData, bankingData, relativesData, recordType } = data

    // Handle reclassification from Natural Person to Entity
    if (recordType === 'entity_business' && editingClient) {
      try {
        // 1. Create entity from client data
        const { data: newEntity, error: entityError } = await supabase
          .from('entities')
          .insert({
            name: clientData.full_name,
            tax_number: clientData.tax_number || null,
            active: clientData.active,
            type: 'Business',
          })
          .select()
          .single()

        if (entityError) throw entityError

        // 2. Re-link policies from client to entity
        const { error: relinkError } = await supabase
          .from('policies')
          .update({ entity_id: newEntity.id, client_id: null })
          .eq('client_id', editingClient.id)

        if (relinkError) {
          console.warn('Warning: Some policies could not be re-linked', relinkError)
        }

        // 3. Delete client record (hard delete - no duplicates)
        const { error: deleteError } = await supabase
          .from('clients')
          .delete()
          .eq('id', editingClient.id)

        if (deleteError) throw deleteError

        // 4. Clear draft
        localStorage.removeItem(`clientForm_${editingClient.id}`)

        // 5. Close form and refetch data
        setShowForm(false)
        setEditingClient(null)
        await refetch()

        // 6. Show success toast
        alert('Record moved to Entities successfully')
      } catch (err) {
        console.error('Error during reclassification:', err)
        alert(`Reclassification failed: ${err.message}`)
      }
      return
    }

    if (editingClient) {
      // Edits: no duplicate check, proceed as before
      try {
        await updateClient(editingClient.id, clientData, addressData, maritalData, bankingData, relativesData)
        setShowForm(false)
        setEditingClient(null)
      } catch (err) {
        console.error('Error updating client:', err)
        alert(`Save failed: ${err.message}`)
      }
      return
    }

    // For now, skip duplicate check — just save directly
    // TODO: Fix edge function JWT authentication issue
    await saveAndSync(data, null)
  }

  const handleMergeConfirm = async (mergedClientData, izuluProfileId) => {
    setShowDuplicateModal(false)
    const formData = {
      ...pendingFormData,
      clientData: mergedClientData,
    }
    await saveAndSync(formData, izuluProfileId)
    setPendingFormData(null)
    setDuplicateCandidates([])
  }

  const handleCreateAnyway = async () => {
    setShowDuplicateModal(false)
    await saveAndSync(pendingFormData, null)
    setPendingFormData(null)
    setDuplicateCandidates([])
  }

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false)
    // pendingFormData intentionally NOT cleared — form stays open with data
    setPendingFormData(null)
    setDuplicateCandidates([])
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === clients.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(clients.map(c => c.id)))
    }
  }

  const handleBulkAssign = async () => {
    if (!assignAdvisorId) return
    setAssigning(true)
    try {
      await bulkAssignAdvisor(assignAdvisorId, [...selectedIds])
      setSelectedIds(new Set())
      setAssignAdvisorId('')
    } finally {
      setAssigning(false)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return

    const message = `Are you sure you want to delete ${selectedIds.size} ${selectedIds.size === 1 ? 'client' : 'clients'}?\n\nThis action cannot be undone. Any policies linked to these clients will become orphaned.`

    if (!window.confirm(message)) return

    setDeletingSelected(true)
    try {
      const idsToDelete = Array.from(selectedIds)
      const { error } = await supabase
        .from('clients')
        .delete()
        .in('id', idsToDelete)

      if (error) throw error

      setSelectedIds(new Set())
      await refetch()
    } catch (err) {
      alert(`Delete failed: ${err.message}`)
    } finally {
      setDeletingSelected(false)
    }
  }

  const handleAdvisorFilterChange = (e) => {
    const id = e.target.value
    setSelectedAdvisorFilter(id ? advisors.find(a => a.id === id) || null : null)
    setPage(1)
  }

  const findDuplicateClients = async () => {
    setDuplicatesLoading(true)
    try {
      // Fetch all clients without pagination
      const { data: allClients, error } = await supabase
        .from('clients')
        .select('id, full_name, id_number, active, created_at')
        .order('full_name')

      if (error) throw error

      // Group by full_name
      const groups = {}
      allClients.forEach(client => {
        const key = client.full_name?.toLowerCase() || 'unnamed'
        if (!groups[key]) {
          groups[key] = []
        }
        groups[key].push(client)
      })

      // Filter to only show groups with 2+ clients
      const duplicates = Object.values(groups)
        .filter(group => group.length > 1)
        .sort((a, b) => b.length - a.length) // Most duplicates first

      setDuplicateGroups(duplicates)
      setShowDuplicateFinder(true)
    } catch (err) {
      alert(`Failed to find duplicates: ${err.message}`)
    } finally {
      setDuplicatesLoading(false)
    }
  }

  const toggleDuplicateSelect = (id) => {
    setSelectedDuplicateIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleDeleteDuplicateClients = async () => {
    if (selectedDuplicateIds.size === 0) return

    const message = `Delete ${selectedDuplicateIds.size} duplicate ${selectedDuplicateIds.size === 1 ? 'client' : 'clients'}?\n\nThis action cannot be undone.`

    if (!window.confirm(message)) return

    setDeletingDuplicates(true)
    try {
      const idsToDelete = Array.from(selectedDuplicateIds)
      const { error } = await supabase
        .from('clients')
        .delete()
        .in('id', idsToDelete)

      if (error) throw error

      // Refetch and clear
      setSelectedDuplicateIds(new Set())
      setShowDuplicateFinder(false)
      await refetch()
    } catch (err) {
      alert(`Delete failed: ${err.message}`)
    } finally {
      setDeletingDuplicates(false)
    }
  }

  const handleSort = (columnKey) => {
    const newOrder = sortBy === columnKey && sortOrder === 'asc' ? 'desc' : 'asc'
    setSort(`${columnKey}:${newOrder}`)
    setPage(1)
  }

  const exportClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('customer_code, full_name, surname, id_number, mobile_number, email_address, date_of_birth, client_status, active')
        .order('full_name')
      if (error) throw error

      const XLSX = await import('xlsx')
      const rows = data.map(c => [
        c.customer_code  || '',
        c.full_name      || '',
        c.surname        || '',
        c.id_number      || '',
        c.mobile_number  || '',
        c.email_address  || '',
        c.date_of_birth  || '',
        c.client_status  || '',
        c.active ? 'Yes' : 'No',
      ])

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([CLIENT_EXPORT_HEADERS, ...rows])
      ws['!cols'] = [
        { wch: 16 }, { wch: 32 }, { wch: 26 }, { wch: 16 },
        { wch: 16 }, { wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 8 },
      ]
      XLSX.utils.book_append_sheet(wb, ws, 'Natural Persons')
      XLSX.writeFile(wb, `natural_persons_export_${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    }
  }

  const handleImport = async (data, { replaceAll = false, onProgress } = {}) => {
    try {
      const result = await importClients(data, { replaceAll, onProgress })
      setShowImport(false)
      const { created, updated, relinked } = result || {}
      const relinkMsg = relinked > 0 ? `\n${relinked} policies relinked by Customer Code.` : ''
      const parts = []
      if (created > 0) parts.push(`${created} new client${created !== 1 ? 's' : ''} created`)
      if (updated > 0) parts.push(`${updated} existing client${updated !== 1 ? 's' : ''} updated`)
      if (parts.length === 0) parts.push('No changes — file matched no records')
      alert(`${parts.join(', ')}.${relinkMsg}`)
    } catch (err) {
      alert(`Import failed: ${err.message}`)
    }
  }

  const statusBadge = (status) => {
    const map = {
      'Active':      'bg-green-100 text-green-700',
      'Inactive':    'bg-red-100 text-red-700',
      'Prospective': 'bg-gray-100 text-gray-600',
    }
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-500'}`}>
        {status || '—'}
      </span>
    )
  }

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={clients.length > 0 && selectedIds.size === clients.length}
          onChange={toggleSelectAll}
          className="rounded"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded"
        />
      )
    },
    {
      key: 'customer_code',
      label: 'Cust. Code',
      sortable: true,
      render: (row) => row.customer_code
        ? <span className="font-mono text-xs font-semibold bg-sfp-orange/10 text-sfp-orange px-1.5 py-0.5 rounded">{row.customer_code}</span>
        : <span className="text-gray-300 text-xs">—</span>
    },
    { key: 'full_name', label: 'Full Name', sortable: true },
    { key: 'id_number', label: 'ID Number', sortable: true, render: (row) => row.id_number || <span className="text-gray-300 text-xs">—</span> },
    { key: 'mobile_number', label: 'Mobile', render: (row) => row.mobile_number || <span className="text-gray-300 text-xs">—</span> },
    { key: 'email_address', label: 'Email', sortable: true, render: (row) => row.email_address || <span className="text-gray-300 text-xs">—</span> },
    {
      key: 'advisors',
      label: 'Advisor',
      render: (row) => row.advisors?.full_name || <span className="text-gray-300 text-xs">—</span>
    },
    {
      key: 'client_status',
      label: 'Status',
      sortable: true,
      render: (row) => statusBadge(row.client_status)
    },
    {
      key: 'policy_count',
      label: 'Policies',
      render: (row) => row.policy_count > 0
        ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">{row.policy_count}</span>
        : <span className="text-gray-300 text-xs">0</span>
    },
    {
      key: 'active',
      label: 'Active',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggleActive(row.id, row.active) }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${row.active ? 'bg-green-500' : 'bg-gray-300'}`}
          title={row.active ? 'Click to deactivate' : 'Click to activate'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.active ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      )
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleRowClick(row) }}
          className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
        >
          Edit
        </button>
      )
    },
  ]

  return (
    <div className="p-4 h-full flex flex-col gap-4" style={{ backgroundColor: isDarkMode ? '#0f172a' : undefined }}>
      {/* Action Bar */}
      <div className="flex justify-between items-center gap-4 flex-shrink-0 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <Button variant="primary" onClick={() => {
            setEditingClient(null)
            setShowForm(true)
          }}>
            + Add Natural Person
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowImport(true)}
          >
            ↑ Import Excel
          </Button>
          <Button variant="secondary" onClick={exportClients}>
            ↓ Export Excel
          </Button>
          <Button variant="secondary" onClick={() => setShowMatcher(true)}>
            🔗 Match Client Codes
          </Button>
          <Button variant="secondary" onClick={handleRelink} disabled={relinking}>
            {relinking ? 'Relinking…' : '🔄 Relink Policies'}
          </Button>
          <Button
            variant="secondary"
            onClick={findDuplicateClients}
            disabled={duplicatesLoading}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            {duplicatesLoading ? 'Scanning...' : '🔍 Find Duplicates'}
          </Button>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333' }}>
                {selectedIds.size} selected
              </span>
              <div className="flex items-center gap-2">
                <select value={assignAdvisorId} onChange={(e) => setAssignAdvisorId(e.target.value)} className="input-field text-sm">
                  <option value="">Assign Advisor…</option>
                  {advisors.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
                </select>
                <Button variant="primary" onClick={handleBulkAssign} disabled={!assignAdvisorId || assigning}>
                  {assigning ? 'Assigning…' : 'Assign'}
                </Button>
              </div>
              <Button
                variant="secondary"
                onClick={handleDeleteSelected}
                disabled={deletingSelected}
                className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
              >
                {deletingSelected ? 'Deleting…' : '🗑️ Delete Selected'}
              </Button>
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1) }}
            className="input-field text-sm"
          >
            <option value="updated_at:desc">Last Modified</option>
            <option value="customer_code:asc">Customer Code A → Z</option>
            <option value="customer_code:desc">Customer Code Z → A</option>
            <option value="created_at:desc">Date Created (Newest)</option>
            <option value="created_at:asc">Date Created (Oldest)</option>
            <option value="surname:asc">Surname A → Z</option>
            <option value="surname:desc">Surname Z → A</option>
            <option value="full_name:asc">Full Name A → Z</option>
            <option value="full_name:desc">Full Name Z → A</option>
            <option value="client_status:asc">Status A → Z</option>
          </select>
          <select value={selectedAdvisorFilter?.id || ''} onChange={handleAdvisorFilterChange} className="input-field text-sm">
            <option value="">All Advisors</option>
            {advisors.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
          </select>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            className="input-field max-w-xs"
          />
        </div>
      </div>

      {/* Table - Takes remaining space */}
      <div className="overflow-hidden flex flex-col flex-1 rounded-lg border shadow-sm" style={{ backgroundColor: isDarkMode ? '#1e293b' : '#ffffff', borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
        <div className="overflow-auto flex-1">
          <Table
            columns={columns}
            data={clients}
            loading={loading}
            onRowClick={handleRowClick}
            isDarkMode={isDarkMode}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>
      </div>

      {/* Pagination */}
      <div className="flex-shrink-0">
        <Pagination
          currentPage={page}
          totalItems={total}
          pageSize={100}
          onPageChange={setPage}
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <ClientForm
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false)
            setEditingClient(null)
          }}
          initialData={editingClient}
          sidebarCollapsed={sidebarCollapsed}
          checkingDuplicates={checkingDuplicates}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Duplicate Modal */}
      {showDuplicateModal && pendingFormData && (
        <IzuluDuplicateModal
          sfpData={pendingFormData.clientData}
          candidates={duplicateCandidates}
          onMerge={handleMergeConfirm}
          onCreateNew={handleCreateAnyway}
          onCancel={handleDuplicateCancel}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {/* Import Modal */}
      {showImport && (
        <ClientImport
          onImport={handleImport}
          onClose={() => setShowImport(false)}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Client Code Matcher Modal */}
      {showMatcher && (
        <ClientCodeMatcher
          onClose={() => setShowMatcher(false)}
          onDone={refetch}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {/* Duplicate Finder Modal */}
      {showDuplicateFinder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" style={{ backgroundColor: isDarkMode ? '#1e293b' : '#ffffff' }}>
            {/* Header */}
            <div className="p-6 border-b" style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
              <h3 className="text-lg font-bold flex justify-between items-center" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                <span>🔍 Find & Clean Duplicates</span>
                <button onClick={() => setShowDuplicateFinder(false)} className="text-2xl leading-none hover:opacity-70">✕</button>
              </h3>
              <p className="text-sm mt-2" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                Found {duplicateGroups.length} group{duplicateGroups.length === 1 ? '' : 's'} with duplicate client names
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {duplicateGroups.length === 0 ? (
                <div className="text-center py-8" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                  No duplicates found! ✓
                </div>
              ) : (
                duplicateGroups.map((group, idx) => (
                  <div key={idx} className="border rounded-lg p-4" style={{ backgroundColor: isDarkMode ? '#0f172a' : '#f9fafb', borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
                    <div className="font-semibold mb-3" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                      "{group[0].full_name}" ({group.length} duplicates)
                    </div>
                    <div className="space-y-2">
                      {group.map(client => (
                        <label key={client.id} className="flex items-center gap-3 p-2 rounded hover:bg-opacity-50" style={{ backgroundColor: selectedDuplicateIds.has(client.id) ? (isDarkMode ? '#334155' : '#f0f9ff') : 'transparent' }}>
                          <input
                            type="checkbox"
                            checked={selectedDuplicateIds.has(client.id)}
                            onChange={() => toggleDuplicateSelect(client.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>
                            <div className="font-medium">{client.full_name}</div>
                            <div className="text-xs" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>
                              {client.id_number && `ID: ${client.id_number}`}
                              {client.id_number && ' • '}
                              {client.active ? '✓ Active' : '○ Inactive'}
                              {' • '}
                              Created: {new Date(client.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex gap-2 justify-between items-center" style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }}>
              <div className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                {selectedDuplicateIds.size > 0 && (
                  <span className="font-semibold" style={{ color: isDarkMode ? '#f1f5f9' : '#333333' }}>
                    {selectedDuplicateIds.size} selected for deletion
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDuplicateFinder(false)}
                  className="px-4 py-2 rounded text-sm font-semibold"
                  style={{ backgroundColor: isDarkMode ? '#334155' : '#f1f5f9', color: isDarkMode ? '#e2e8f0' : '#333333' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteDuplicateClients}
                  disabled={selectedDuplicateIds.size === 0 || deletingDuplicates}
                  className="px-4 py-2 rounded text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingDuplicates ? 'Deleting...' : `🗑️ Delete ${selectedDuplicateIds.size > 0 ? selectedDuplicateIds.size : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
